import { supabase } from '@/lib/supabase';
import { getAuthHeaders, withOrchestrationApi } from '@/lib/orchestration-api';
import { getVisitMetrics } from '@/features/analytics/api/visits';

export type OrderStatus =
  | 'CART_DRAFT'
  | 'ORDER_SUBMITTED'
  | 'CONFIRMED_BY_MANAGER'
  | 'DELIVERY_REQUEST_STARTED'
  | 'RIDER_CONFIRMED_DIGITAL'
  | 'RIDER_CONFIRMED_MANUAL'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'CANCELLED';

export interface OrderItem {
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  variant?: string;
}

export interface Order {
  id: string;
  order_number?: number;
  order_reference?: string;
  customer_phone: string;
  customer_name?: string;
  delivery_address: string;
  delivery_notes?: string;
  delivery_method?: 'delivery' | 'pickup';
  branch_id: string;
  status: OrderStatus;
  items: OrderItem[];
  total_amount: number;
  delivery_fee?: number;
  vat_amount?: number;
  created_at: string;
  updated_at: string;
  version: number;
  current_rider_id?: string;
  rider_id?: string;
  current_rider_name?: string;
}

export interface OrdersResponse {
  success: boolean;
  data: Order[];
  count: number;
}

export interface OrderResponse {
  success: boolean;
  data: Order;
}

export interface ListOrdersOptions {
  status?: OrderStatus | 'ALL';
  branchId?: string;
  search?: string;
}

export interface CreateAdminOrderInput {
  customer_phone?: string | null;
  customer_name?: string | null;
  delivery_address?: string | null;
  delivery_notes?: string | null;
  branch_id?: string | null;
  items: OrderItem[];
  total?: number;
  delivery_fee?: number;
  tax?: number;
  trigger_source?: 'call_button' | 'cart_checkout' | 'whatsapp';
}

export interface UpdateStatusRequest {
  status: OrderStatus;
  actor_type: 'admin' | 'rider' | 'system' | 'customer' | 'webhook';
  actor_id: string;
  confirmation_method?: 'DIGITAL' | 'MANUAL';
  expected_version?: number;
  notes?: string;
}

export interface AssignRiderRequest {
  rider_id: string;
  admin_id: string;
  notes?: string;
  send_out_now?: boolean;
  expected_version?: number;
}

export interface DeleteOrderRequest {
  actor_type: 'admin' | 'customer' | 'rider';
  actor_id: string;
  reason?: string;
  confirm: true;
}

export interface RestoreOrderRequest {
  actor_type: 'admin' | 'customer' | 'rider';
  actor_id: string;
}

function mapOrderItem(raw: Record<string, unknown>): OrderItem {
  return {
    product_id: String(raw.product_id || ''),
    product_name: String(raw.product_name || ''),
    quantity: Number(raw.quantity || 0),
    unit_price: Number(raw.unit_price || raw.price || 0),
    variant: raw.variant ? String(raw.variant) : undefined,
  };
}

function normalizeOrder(raw: Record<string, unknown>): Order {
  const itemsSource = Array.isArray(raw.items)
    ? raw.items
    : Array.isArray(raw.order_items)
      ? raw.order_items
      : [];
  const deliveryAssignments = Array.isArray(raw.delivery_assignments)
    ? raw.delivery_assignments
    : [];
  const latestAssignment = deliveryAssignments[0] as Record<string, unknown> | undefined;
  const metadata = (raw.metadata || {}) as Record<string, unknown>;

  return {
    id: String(raw.id || ''),
    order_number: raw.order_number !== undefined && raw.order_number !== null
      ? Number(raw.order_number)
      : undefined,
    order_reference: raw.order_reference ? String(raw.order_reference) : undefined,
    customer_phone: String(raw.customer_phone || ''),
    customer_name: raw.customer_name ? String(raw.customer_name) : undefined,
    delivery_address: String(raw.delivery_address || ''),
    delivery_notes: raw.delivery_notes ? String(raw.delivery_notes) : undefined,
    delivery_method: raw.delivery_method === 'pickup' ? 'pickup' : 'delivery',
    branch_id: String(raw.branch_id || ''),
    status: String(raw.status || 'ORDER_SUBMITTED') as OrderStatus,
    items: itemsSource.map((item) => mapOrderItem(item as Record<string, unknown>)),
    total_amount: Number(raw.total_amount || raw.total || 0),
    delivery_fee: raw.delivery_fee !== undefined ? Number(raw.delivery_fee) : undefined,
    vat_amount: raw.vat_amount !== undefined ? Number(raw.vat_amount) : undefined,
    created_at: String(raw.created_at || new Date().toISOString()),
    updated_at: String(raw.updated_at || raw.created_at || new Date().toISOString()),
    version: Number(raw.state_version || raw.version || 1),
    current_rider_id: raw.current_rider_id
      ? String(raw.current_rider_id)
      : raw.rider_id
        ? String(raw.rider_id)
        : latestAssignment?.rider_id
          ? String(latestAssignment.rider_id)
          : undefined,
    rider_id: raw.rider_id
      ? String(raw.rider_id)
      : latestAssignment?.rider_id
        ? String(latestAssignment.rider_id)
        : undefined,
    current_rider_name: raw.current_rider_name
      ? String(raw.current_rider_name)
      : latestAssignment?.rider_name
        ? String(latestAssignment.rider_name)
        : metadata.assigned_rider_name
          ? String(metadata.assigned_rider_name)
          : undefined,
  };
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (typeof payload === 'string') {
    const compactPayload = payload.trim().slice(0, 120);
    throw new Error(
      compactPayload.startsWith('<')
        ? 'Orders API returned HTML instead of JSON. Check the Supabase functions base URL configuration.'
        : compactPayload || 'Orders API returned a non-JSON response.'
    );
  }

  if (!response.ok) {
    throw new Error(payload.error || payload.message || 'Request failed');
  }
  return payload as T;
}

function resolveAbsoluteApiUrl(path: string): string {
  const resolved = withOrchestrationApi(path);

  if (/^https?:\/\//i.test(resolved)) {
    return resolved;
  }

  throw new Error(
    `Orders API base URL is not configured. Refusing to call relative endpoint: ${resolved}`
  );
}

async function callOrdersApi(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  options?: {
    params?: Record<string, string | undefined>;
    body?: Record<string, unknown>;
  }
) {
  const url = new URL(resolveAbsoluteApiUrl('/orders'));
  Object.entries(options?.params || {}).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });

  const headers = await getAuthHeaders();
  const response = await fetch(url.toString(), {
    method,
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: options?.body ? JSON.stringify(options.body) : undefined,
  });

  return parseResponse<Record<string, unknown>>(response);
}

async function callEdgeFunction<T>(
  name: string,
  body: Record<string, unknown>
): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(resolveAbsoluteApiUrl(`/${name}`), {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  return parseResponse<T>(response);
}

export async function listOrders(options: ListOrdersOptions = {}): Promise<Order[]> {
  const payload = await callOrdersApi('GET', {
    params: {
      status: options.status && options.status !== 'ALL' ? options.status : undefined,
      branch_id: options.branchId,
      search: options.search?.trim() || undefined,
    },
  });

  const rawOrders = Array.isArray(payload.data) ? payload.data : [];
  return rawOrders.map((order) => normalizeOrder(order as Record<string, unknown>));
}

export async function getOrdersByStatus(status: OrderStatus, branchId?: string): Promise<Order[]> {
  return listOrders({ status, branchId });
}

export async function getOrderById(orderId: string): Promise<Order | null> {
  const payload = await callOrdersApi('GET', {
    params: { id: orderId },
  });

  if (!payload.data || Array.isArray(payload.data)) {
    return null;
  }

  return normalizeOrder(payload.data as Record<string, unknown>);
}

export async function createAdminOrder(input: CreateAdminOrderInput): Promise<Order> {
  const payload = await callOrdersApi('POST', {
    body: {
      customer_phone: input.customer_phone?.trim() || null,
      customer_name: input.customer_name?.trim() || null,
      delivery_address: input.delivery_address?.trim() || 'PENDING_ADDRESS',
      delivery_notes: input.delivery_notes?.trim() || null,
      branch_id: input.branch_id || null,
      delivery_fee: input.delivery_fee ?? 0,
      tax: input.tax ?? 0,
      total: input.total ?? input.items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0),
      trigger_source: input.trigger_source || 'call_button',
      idempotency_key: `admin_${crypto.randomUUID()}`,
      items: input.items,
    },
  });

  if (!payload.data || Array.isArray(payload.data)) {
    throw new Error('Failed to create order');
  }

  return normalizeOrder(payload.data as Record<string, unknown>);
}

export async function updateOrderStatus(orderId: string, request: UpdateStatusRequest): Promise<Order> {
  const payload = await callEdgeFunction<{ success: boolean; data: Record<string, unknown>; error?: string }>(
    'order-status',
    {
      order_id: orderId,
      ...request,
    }
  );

  if (!payload.success || !payload.data) {
    throw new Error(payload.error || 'Failed to update order');
  }

  return normalizeOrder(payload.data);
}

export async function updateOrderDetails(
  orderId: string,
  updates: Record<string, unknown>
): Promise<Order> {
  const payload = await callOrdersApi('PATCH', {
    body: {
      id: orderId,
      ...updates,
    },
  });

  if (!payload.data || Array.isArray(payload.data)) {
    throw new Error('Failed to update order');
  }

  return normalizeOrder(payload.data as Record<string, unknown>);
}

export async function assignRider(orderId: string, request: AssignRiderRequest): Promise<Order> {
  const payload = await callEdgeFunction<{ success: boolean; data: Record<string, unknown>; error?: string }>(
    'order-assign',
    {
      order_id: orderId,
      ...request,
    }
  );

  if (!payload.success || !payload.data) {
    throw new Error(payload.error || 'Failed to assign rider');
  }

  return normalizeOrder(payload.data);
}

export async function getAllOrders(options: ListOrdersOptions = {}): Promise<Order[]> {
  return listOrders(options);
}

export async function getDashboardStats(): Promise<{
  totalOrders: number;
  pendingOrders: number;
  outForDelivery: number;
  deliveredToday: number;
  siteVisits: number;
  productVisits: number;
}> {
  const [orders, visitMetrics] = await Promise.all([
    getAllOrders(),
    getVisitMetrics().catch(() => ({ siteVisitCount: 0, productVisitCount: 0 })),
  ]);
  const today = new Date().toDateString();

  return {
    totalOrders: orders.length,
    pendingOrders: orders.filter((order) =>
      [
        'ORDER_SUBMITTED',
        'CONFIRMED_BY_MANAGER',
        'DELIVERY_REQUEST_STARTED',
        'RIDER_CONFIRMED_DIGITAL',
        'RIDER_CONFIRMED_MANUAL',
      ].includes(order.status)
    ).length,
    outForDelivery: orders.filter((order) => order.status === 'OUT_FOR_DELIVERY').length,
    deliveredToday: orders.filter(
      (order) => order.status === 'DELIVERED' && new Date(order.updated_at).toDateString() === today
    ).length,
    siteVisits: visitMetrics.siteVisitCount,
    productVisits: visitMetrics.productVisitCount,
  };
}

export async function deleteOrder(orderId: string, request: DeleteOrderRequest): Promise<Order> {
  await callOrdersApi('DELETE', {
    params: { id: orderId },
    body: { ...request, id: orderId },
  });

  const deletedOrder = await getOrderById(orderId);
  if (!deletedOrder) {
    return {
      id: orderId,
      customer_phone: '',
      delivery_address: '',
      branch_id: '',
      status: 'CANCELLED',
      items: [],
      total_amount: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      version: 1,
    };
  }

  return deletedOrder;
}

export async function restoreOrder(orderId: string, _request: RestoreOrderRequest): Promise<Order> {
  if (!supabase) {
    throw new Error('Supabase client not available');
  }

  const { data, error } = await (supabase as any)
    .from('orders')
    .update({ deleted_at: null })
    .eq('id', orderId)
    .select('*, order_items(*), delivery_assignments(*)')
    .single();

  if (error) {
    throw new Error(error.message || 'Failed to restore order');
  }

  return normalizeOrder(data as unknown as Record<string, unknown>);
}

export async function getDeletedOrders(branchId?: string): Promise<Order[]> {
  if (!supabase) {
    throw new Error('Supabase client not available');
  }

  let query = supabase
    .from('orders')
    .select('*, order_items(*), delivery_assignments(*)')
    .not('deleted_at', 'is', null)
    .order('created_at', { ascending: false });

  if (branchId) {
    query = query.eq('branch_id', branchId);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message || 'Failed to fetch deleted orders');
  }

  return (data || []).map((order) => normalizeOrder(order as unknown as Record<string, unknown>));
}

export async function permanentlyDeleteOrder(
  orderId: string,
  actorType: 'admin' | 'customer' | 'rider',
  actorId: string
): Promise<void> {
  await callOrdersApi('DELETE', {
    params: { id: orderId, permanent: 'true' },
    body: { id: orderId, permanent: true, actor_type: actorType, actor_id: actorId, confirm: true },
  });
}
